import { useState, useEffect, useRef } from 'react';
import { Play, Square, Clock, Plus, Edit2, Trash2, X } from 'lucide-react';
import { API } from '../lib/api';

interface Project {
  projectId: string;
  name: string;
  clientId: string;
}

interface TimeEntry {
  entryId: string;
  projectId: string;
  description: string;
  hours: number;
  date: string;
  billable: boolean;
  timestamp?: number;
}

export default function TimerPage() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [description, setDescription] = useState('');
  const [billable, setBillable] = useState(true);
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [recentEntries, setRecentEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [manualHours, setManualHours] = useState('');
  const [error, setError] = useState('');
  const startTimeRef = useRef<number>(0);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (running) {
      interval = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [running]);

  const loadInitialData = async () => {
    try {
      const [projectsData, entries] = await Promise.all([
        API.getProjects(),
        API.getTimeEntries(),
      ]);
      setProjects(projectsData);
      setRecentEntries(entries.slice(0, 10));
    } catch (err) {
      setError('Failed to load data');
    }
  };

  const start = () => {
    if (!selectedProject) {
      setError('Please select a project');
      return;
    }
    setError('');
    startTimeRef.current = Date.now();
    setRunning(true);
    setElapsed(0);
  };

  const stop = async () => {
    setLoading(true);
    setError('');
    try {
      const hours = Math.round((elapsed / 3600) * 100) / 100;
      if (hours > 0) {
        await API.createTimeEntry({
          projectId: selectedProject,
          description,
          hours,
          billable,
        });
      }
      setRunning(false);
      setElapsed(0);
      setDescription('');
      await loadRecentEntries();
    } catch (err: any) {
      setError(err.message || 'Failed to save time entry');
    } finally {
      setLoading(false);
    }
  };

  const addManualEntry = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!selectedProject) {
      setError('Please select a project');
      return;
    }
    const hours = parseFloat(manualHours);
    if (!hours || hours <= 0 || hours > 24) {
      setError('Hours must be between 0 and 24');
      return;
    }
    setLoading(true);
    try {
      await API.createTimeEntry({
        projectId: selectedProject,
        description,
        hours,
        billable,
      });
      setShowManualEntry(false);
      setManualHours('');
      setDescription('');
      await loadRecentEntries();
    } catch (err: any) {
      setError(err.message || 'Failed to add time entry');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEntry = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingEntry) return;
    setLoading(true);
    setError('');
    try {
      await API.updateTimeEntry(editingEntry.timestamp?.toString() || editingEntry.entryId, {
        hours: editingEntry.hours,
        description: editingEntry.description,
        billable: editingEntry.billable,
      });
      setEditingEntry(null);
      await loadRecentEntries();
    } catch (err: any) {
      setError(err.message || 'Failed to update entry');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEntry = async (entryId: string) => {
    if (!confirm('Delete this time entry?')) return;
    setError('');
    try {
      await API.deleteTimeEntry(entryId);
      await loadRecentEntries();
    } catch (err: any) {
      setError(err.message || 'Failed to delete entry');
    }
  };

  const loadRecentEntries = async () => {
    const entries = await API.getTimeEntries();
    setRecentEntries(entries.slice(0, 10));
  };

  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const formatHours = (hours: number) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError('')}><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl font-bold text-gray-900">Time Tracker</h2>
          <div className="flex items-center gap-2">
            {running && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                <span className="w-2 h-2 mr-2 bg-red-500 rounded-full animate-pulse" />
                Recording
              </span>
            )}
            {!running && (
              <button
                onClick={() => setShowManualEntry(true)}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                <Plus className="w-4 h-4 mr-1" />
                Manual Entry
              </button>
            )}
          </div>
        </div>

        <div className="max-w-2xl mx-auto space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Project</label>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              disabled={running}
              className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">Select a project</option>
              {projects.map((p) => (
                <option key={p.projectId} value={p.projectId}>{p.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={running}
              placeholder="What are you working on?"
              className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={billable}
                onChange={(e) => setBillable(e.target.checked)}
                disabled={running}
                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-700">Billable</span>
            </label>
          </div>

          <div className="text-center py-8">
            <div className="text-6xl font-mono font-bold text-gray-900 mb-8 tracking-tight">
              {formatTime(elapsed)}
            </div>
            <button
              onClick={running ? stop : start}
              disabled={loading || (!selectedProject && !running)}
              className={`inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-lg shadow-sm text-white transition-colors ${
                running
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-indigo-600 hover:bg-indigo-700'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading ? (
                <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
              ) : running ? (
                <Square className="w-5 h-5 mr-2" />
              ) : (
                <Play className="w-5 h-5 mr-2" />
              )}
              {loading ? 'Saving...' : running ? 'Stop & Save' : 'Start Timer'}
            </button>
          </div>
        </div>
      </div>

      {showManualEntry && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Manual Time Entry</h3>
          <form onSubmit={addManualEntry} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hours</label>
                <input
                  type="number"
                  step="0.25"
                  min="0.25"
                  max="24"
                  value={manualHours}
                  onChange={(e) => setManualHours(e.target.value)}
                  placeholder="e.g., 2.5"
                  className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What did you work on?"
                  className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={billable}
                  onChange={(e) => setBillable(e.target.checked)}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">Billable</span>
              </label>
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setShowManualEntry(false); setManualHours(''); }}
                className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
              >
                Add Entry
              </button>
            </div>
          </form>
        </div>
      )}

      {editingEntry && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Time Entry</h3>
          <form onSubmit={handleUpdateEntry} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hours</label>
                <input
                  type="number"
                  step="0.25"
                  min="0.25"
                  max="24"
                  value={editingEntry.hours}
                  onChange={(e) => setEditingEntry({ ...editingEntry, hours: parseFloat(e.target.value) || 0 })}
                  className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={editingEntry.description}
                  onChange={(e) => setEditingEntry({ ...editingEntry, description: e.target.value })}
                  className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editingEntry.billable}
                  onChange={(e) => setEditingEntry({ ...editingEntry, billable: e.target.checked })}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">Billable</span>
              </label>
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setEditingEntry(null)}
                className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
              >
                Save Changes
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Today's Entries</h3>
        <div className="space-y-3">
          {recentEntries.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">No time entries yet. Start tracking!</p>
            </div>
          ) : (
            recentEntries.map((entry) => (
              <div key={entry.entryId} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center flex-1">
                  <Clock className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {projects.find((p) => p.projectId === entry.projectId)?.name || entry.projectId}
                    </p>
                    <p className="text-sm text-gray-500">{entry.description || 'No description'}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <span className="text-sm font-medium text-gray-900 mr-2">{formatHours(entry.hours || 0)}</span>
                  {entry.billable && <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded mr-2">Billable</span>}
                  <button
                    onClick={() => setEditingEntry(entry)}
                    className="p-1.5 text-gray-400 hover:text-indigo-600 mr-1"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteEntry(entry.timestamp?.toString() || entry.entryId)}
                    className="p-1.5 text-gray-400 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
